<!--
  TARIFFE › PACCHETTO VALET.

  Il listino valet non sta sulle colonne del partner come le altre tariffe:
  sta nelle fasce km di `valet_km_band`. Ogni fascia ha il prezzo Simba di UN
  pickup, a scaglioni di volume (uno, due, tre o più); accanto il partner
  imposta il suo prezzo di vendita, mai inferiore a quello Simba.

  ⚠️ I prezzi NON sono annui e la durata non li moltiplica: il pacchetto costa
  `prezzo unitario × numero di pickup`, i mesi decidono solo la scadenza. I
  campi si chiamano ancora `annual_price_*` per ragioni storiche.

  Ogni fascia ha anche il prezzo AL KM dei km oltre quelli compresi nel
  pacchetto (colonna «Km in più»), Simba e venditore: una corsa col valet paga
  solo i km di ogni tratta oltre quelli compresi.

  ⚠️ Il prezzo del venditore esiste solo sulle fasce DEL PARTNER. Un partner
  senza listino proprio usa quello generale di Simba (fasce senza partner):
  lì un prezzo del venditore varrebbe per tutti, quindi si mostra soltanto e
  si rimanda a Simba.
-->
<template>
  <section class="vts">
    <header class="vts__head">
      <h2 class="vts__title">
        Pacchetto valet
      </h2>
      <p class="vts__note">
        Prezzo di un pickup{{ modifica ? '' : ' che paga il cliente' }}, secondo la distanza fra
        la casa del cliente e la sede e quanti pickup comprende il pacchetto.
        Il pacchetto costa prezzo × numero di pickup; i mesi decidono la
        scadenza, non il prezzo.
      </p>
    </header>

    <div
      v-if="stato === 'carico'"
      class="vts__stato"
    >
      Carico il listino valet…
    </div>
    <div
      v-else-if="stato === 'errore'"
      class="vts__stato vts__stato--ko"
      role="alert"
    >
      Non sono riuscito a caricare il listino valet.
      <el-button
        size="small"
        @click="carica"
      >
        Riprova
      </el-button>
    </div>
    <div
      v-else-if="!fasce.length"
      class="vts__stato"
    >
      Sul tuo contratto non c'è un listino valet. Parlane con Simba.
    </div>

    <template v-else>
      <div
        v-if="globale && modifica"
        class="vts__banner"
      >
        Usi il listino valet generale di Simba: i prezzi Simba li vedi qui, ma
        per impostare i tuoi serve un listino dedicato al tuo contratto.
        Chiedilo a Simba.
      </div>

      <div class="vts__scroll">
        <table class="vts__tab">
          <thead>
            <tr>
              <th>Fascia</th>
              <th
                v-for="n in OPZIONI"
                :key="n"
              >
                {{ n === 3 ? '3 o più pickup' : `${n} pickup` }}
              </th>
              <th>Km in più</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="f in fasce"
              :key="f.id"
            >
              <td class="vts__fascia">
                {{ etichettaFascia(f) }}
              </td>
              <td
                v-for="n in OPZIONI"
                :key="n"
                class="vts__cell"
              >
                <!-- Dipendente e venditore: solo il prezzo del partner. -->
                <template v-if="!modifica">
                  <span
                    v-if="!globale && valorizzato(f[`partner_annual_price_${n}`])"
                    class="vts__prezzo"
                  >
                    {{ euro(f[`partner_annual_price_${n}`]) }} a pickup
                  </span>
                  <span
                    v-else
                    class="vts__dash"
                  >non impostato</span>
                </template>
                <template v-else-if="valorizzato(f[`annual_price_${n}`])">
                  <div class="vts__simba">
                    Simba {{ euro(f[`annual_price_${n}`]) }} a pickup
                  </div>
                  <template v-if="!globale">
                    <el-input
                      v-model="bozza[f.id][n]"
                      type="number"
                      step="0.01"
                      min="0"
                      size="small"
                      placeholder="Il tuo prezzo"
                      :aria-label="`${etichettaFascia(f)}, ${n === 3 ? '3 o più pickup' : `${n} pickup`}: il tuo prezzo per pickup`"
                    >
                      <template #prepend>
                        €
                      </template>
                    </el-input>
                    <div
                      v-if="errori[chiave(f.id, n)]"
                      class="vts__err"
                    >
                      {{ errori[chiave(f.id, n)] }}
                    </div>
                    <div
                      v-else-if="valorizzato(bozza[f.id][n])"
                      class="vts__mese"
                    >
                      = {{ euro(Number(bozza[f.id][n]) * n) }} per {{ n }} pickup
                    </div>
                  </template>
                </template>
                <span
                  v-else
                  class="vts__dash"
                >non previsto</span>
              </td>
              <!-- Km oltre il pacchetto: prezzo al km della fascia. -->
              <td class="vts__cell">
                <template v-if="!modifica">
                  <span
                    v-if="!globale && valorizzato(f.partner_excess_price_per_km)"
                    class="vts__prezzo"
                  >{{ euro(f.partner_excess_price_per_km) }}/km</span>
                  <span
                    v-else
                    class="vts__dash"
                  >non impostato</span>
                </template>
                <template v-else>
                  <div class="vts__simba">
                    Simba {{ valorizzato(f.excess_price_per_km) ? `${euro(f.excess_price_per_km)}/km` : 'non impostato' }}
                  </div>
                  <template v-if="!globale">
                    <el-input
                      v-model="bozza[f.id].km"
                      type="number"
                      step="0.01"
                      min="0"
                      size="small"
                      placeholder="Il tuo prezzo"
                      :aria-label="`${etichettaFascia(f)}, km oltre il pacchetto: il tuo prezzo al km`"
                    >
                      <template #prepend>
                        €/km
                      </template>
                    </el-input>
                    <div
                      v-if="errori[chiave(f.id, 'km')]"
                      class="vts__err"
                    >
                      {{ errori[chiave(f.id, 'km')] }}
                    </div>
                  </template>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="!globale && modifica"
        class="vts__azioni"
      >
        <span class="vts__hint">Il tuo prezzo è per UN pickup; «Km in più» è al km. Lascialo vuoto se non vendi quella variante.</span>
        <el-button
          type="primary"
          :loading="salvataggio"
          :disabled="!modificate.length"
          @click="salva"
        >
          Salva prezzi valet
        </el-button>
      </div>
    </template>
  </section>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import partnerAPI from '../services/partnerAPI.js';
import { normalize } from '../services/crud';
import { isLoggedStore } from '../store/logged';

const OPZIONI = [1, 2, 3];

const valorizzato = (v) => v !== null && v !== undefined && v !== '' && !Number.isNaN(Number(v));
const euro = (v) =>
  `${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const chiave = (id, n) => `${id}-${n}`;

export default {
  name: 'ValetTariffeSection',

  props: {
    // false = dipendente e venditore: solo i prezzi del partner, in lettura.
    modifica: { type: Boolean, default: true },
  },

  setup() {
    const store = isLoggedStore();
    const stato = ref('carico'); // carico | ok | errore
    const tutte = ref([]);
    const bozza = reactive({});
    const errori = reactive({});
    const salvataggio = ref(false);

    // Stessa regola del backend: vale il listino del partner se ne ha uno,
    // altrimenti tutto quello generale. Mai mescolati.
    const proprie = computed(() => tutte.value.filter((f) => f.partnerId && f.partnerId === store.partner?.id));
    const globale = computed(() => !proprie.value.length);
    const fasce = computed(() =>
      (proprie.value.length ? proprie.value : tutte.value.filter((f) => !f.partnerId))
        .slice()
        .sort((a, z) => Number(a.from_km) - Number(z.from_km)),
    );

    const riempiBozza = () => {
      fasce.value.forEach((f) => {
        bozza[f.id] = {};
        OPZIONI.forEach((n) => {
          const v = f[`partner_annual_price_${n}`];
          bozza[f.id][n] = valorizzato(v) ? String(v) : '';
        });
        bozza[f.id].km = valorizzato(f.partner_excess_price_per_km) ? String(f.partner_excess_price_per_km) : '';
      });
      Object.keys(errori).forEach((k) => delete errori[k]);
    };

    const carica = async () => {
      stato.value = 'carico';
      try {
        const res = await partnerAPI.getValetBands();
        tutte.value = normalize(res).rows;
        riempiBozza();
        stato.value = 'ok';
      } catch (e) {
        console.error('Listino valet non caricato:', e);
        stato.value = 'errore';
      }
    };
    onMounted(carica);

    const etichettaFascia = (f) =>
      f.to_km === null || f.to_km === undefined
        ? `oltre ${Number(f.from_km)} km`
        : `${Number(f.from_km)}–${Number(f.to_km)} km`;

    // I campi del venditore di una fascia: i tre prezzi annuali e il €/km.
    const CAMPI = [
      ...OPZIONI.map((n) => ({ chiave: n, campo: `partner_annual_price_${n}`, simba: `annual_price_${n}` })),
      { chiave: 'km', campo: 'partner_excess_price_per_km', simba: 'excess_price_per_km' },
    ];
    const numero = (v) => (valorizzato(v) ? Number(v) : null);

    // Le fasce con almeno un prezzo diverso da quello salvato.
    const modificate = computed(() =>
      globale.value
        ? []
        : fasce.value.filter((f) => CAMPI.some((c) => numero(f[c.campo]) !== numero(bozza[f.id]?.[c.chiave]))),
    );

    // Il controllo lo rifà il server; qui serve a dirlo accanto al campo.
    const valida = () => {
      Object.keys(errori).forEach((k) => delete errori[k]);
      modificate.value.forEach((f) => {
        CAMPI.forEach((c) => {
          const v = bozza[f.id][c.chiave];
          if (!valorizzato(v)) return;
          const simba = f[c.simba];
          const unita = c.chiave === 'km' ? '/km' : '';
          if (Number(v) < 0) errori[chiave(f.id, c.chiave)] = 'Non può essere negativo';
          else if (valorizzato(simba) && Number(v) < Number(simba)) {
            errori[chiave(f.id, c.chiave)] = `Non può essere inferiore al prezzo Simba (${euro(simba)}${unita})`;
          }
        });
      });
      return !Object.keys(errori).length;
    };

    const salva = async () => {
      if (!valida()) {
        ElMessage({ type: 'error', message: 'Controlla i prezzi segnalati.' });
        return;
      }
      salvataggio.value = true;
      const daSalvare = modificate.value;
      const esiti = await Promise.allSettled(
        daSalvare.map((f) => {
          const prezzi = {};
          CAMPI.forEach((c) => {
            prezzi[c.campo] = numero(bozza[f.id][c.chiave]);
          });
          return partnerAPI.setValetPartnerPrices(f.id, prezzi);
        }),
      );
      const falliti = esiti.filter((e) => e.status === 'rejected');
      if (falliti.length) {
        const motivo = falliti[0].reason?.response?.data?.message;
        ElMessage({
          type: 'error',
          showClose: true,
          message: `${falliti.length} fasce non salvate${motivo ? `: ${motivo}` : '.'}`,
        });
      } else {
        ElMessage({ type: 'success', message: 'Prezzi valet salvati.' });
      }
      salvataggio.value = false;
      await carica();
    };

    return {
      OPZIONI, stato, fasce, globale, bozza, errori, salvataggio, modificate,
      carica, salva, etichettaFascia, valorizzato, euro, chiave,
    };
  },
};
</script>

<style scoped>
.vts { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; box-shadow: var(--el-box-shadow-lighter); overflow: hidden; }
.vts__head { padding: 13px 18px; border-bottom: 1px solid var(--color-border); }
.vts__title { margin: 0; font-size: 14.5px; font-weight: 600; }
.vts__note { margin: 3px 0 0; font-size: 12.5px; line-height: 1.5; color: var(--color-text-secondary); max-width: 72ch; }

.vts__stato { padding: 16px 18px; font-size: 13px; color: var(--color-text-secondary); }
.vts__stato--ko { display: flex; align-items: center; gap: 12px; color: var(--state-danger); font-weight: 600; }
.vts__banner {
  margin: 14px 18px 0; padding: 10px 13px; font-size: 12.5px; line-height: 1.5; border-radius: 6px;
  color: #0f4a7a; background: var(--state-active-bg); border: 1px solid var(--state-active-line);
}

.vts__scroll { overflow-x: auto; padding: 6px 18px 4px; }
.vts__tab { width: 100%; min-width: 820px; border-collapse: collapse; }
.vts__tab th {
  text-align: left; padding: 10px 8px 6px; border-bottom: 1px solid var(--color-border);
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary);
}
.vts__tab td { padding: 9px 8px; vertical-align: top; border-bottom: 1px solid var(--color-bg-tertiary); }
.vts__tab tr:last-child td { border-bottom: 0; }
.vts__fascia { font-size: 13.5px; font-weight: 600; white-space: nowrap; }
.vts__cell { min-width: 170px; }
.vts__cell :deep(.el-input) { max-width: 170px; }
.vts__simba { margin-bottom: 5px; font-size: 12px; color: var(--color-text-secondary); font-variant-numeric: tabular-nums; }
.vts__mese { margin-top: 3px; font-size: 11.5px; color: var(--color-text-muted); font-variant-numeric: tabular-nums; }
.vts__err { margin-top: 3px; font-size: 11.5px; font-weight: 600; color: var(--state-danger); }
.vts__dash { font-size: 12px; color: var(--color-text-muted); }
.vts__prezzo { font-size: 13px; font-weight: 600; font-variant-numeric: tabular-nums; }

.vts__azioni { display: flex; align-items: center; justify-content: flex-end; gap: 12px; flex-wrap: wrap; padding: 10px 18px 14px; }
.vts__hint { font-size: 12px; color: var(--color-text-muted); }
</style>
